import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMyPermissions } from '@app/hooks/api/usePermissions';
import { useAdminStore } from '@app/store/useAdminStore';
import {
  setUserScope,
  clearUserScope,
  getUserScope
} from '@app/store/userScopedStorage';
import {
  useEventFiltersStore,
  resetEventFiltersStore
} from '@app/store/useEventFiltersStore';
import { useFormStore, resetFormStore } from '@app/store/useFormStore';
import {
  useFormEntriesStore,
  resetFormEntriesStore
} from '@app/store/useFormEntriesStore';
import useAuth from '@app/hooks/useAuth';

/**
 * Synchronise les permissions React Query → Zustand store et pilote
 * l'isolation des stores user-scoped (cf. `userScopedStorage`).
 *
 * À placer dans le layout principal (LayoutCometeLink).
 */
export const useAdminBootstrap = () => {
  const { isAuthenticated, user } = useAuth();
  const userId = user?.id;
  const userRole = user?.role;
  const setPermissions = useAdminStore((s) => s.setPermissions);
  const clear = useAdminStore((s) => s.clear);
  const { data: permissions, isPlaceholderData } = useMyPermissions();
  const navigate = useNavigate();

  // Mismatch userId → clear avant l'arrivée des nouvelles permissions. Sans
  // cet effet, sur device partagé (kiosque OnTime), un user voyait brièvement
  // le menu du précédent le temps du refetch (CW-21903).
  //
  // CW-22023 retour QA : on navigue aussi vers '/' au switch d'user. Sans ça,
  // si le nouvel user n'a pas accès à la route courante (cas legitime depuis
  // le fix CW-22023 qui restreint correctement les permissions), il atterrit
  // sur AccessError plein écran au lieu d'une page de sa propre racine. Un
  // switch d'user = nouveau contexte = on repart de la racine, le routing
  // global redirige ensuite vers la home selon le rôle.
  useEffect(() => {
    if (!isAuthenticated || userId === undefined) return;
    const { userId: storedUserId } = useAdminStore.getState();
    if (storedUserId !== null && storedUserId !== userId) {
      clear();
      navigate('/');
    }
  }, [userId, isAuthenticated, clear, navigate]);

  // Isolation localStorage par utilisateur : pose le scope userId et déclenche
  // l'hydratation des stores `userScopedStorage` (filtres MCE, drafts de
  // formulaires, etc.). Sans ce câblage, ces stores restent vides puisqu'ils
  // sont en `skipHydration: true`. Cf. `userScopedStorage.ts` pour le pourquoi.
  //
  // Switch d'utilisateur (multi-tenant, sélection de société sans logout) : on
  // libère l'ancien scope ET on reset le state mémoire AVANT le rehydrate du
  // nouveau scope. Sinon, si le nouvel user n'a rien en localStorage, le merge
  // par défaut de Zustand persist (`{ ...current, ...persisted }`) laisse le
  // state mémoire de l'ancien user intact → fuite cross-user.
  useEffect(() => {
    if (!isAuthenticated || userId === undefined) return;
    const previousScope = getUserScope();
    const nextScope = String(userId);
    if (previousScope === nextScope) return;

    clearUserScope();
    resetEventFiltersStore();
    resetFormStore();
    resetFormEntriesStore();

    setUserScope(userId);
    useEventFiltersStore.persist.rehydrate();
    useFormStore.persist.rehydrate();
    useFormEntriesStore.persist.rehydrate();
  }, [isAuthenticated, userId]);

  useEffect(() => {
    if (!isAuthenticated || userId === undefined || !userRole || !permissions) return;
    // CW-22023 (defense en profondeur) : si placeholderData global keepPreviousData
    // refait surface (override manquant dans useMyPermissions, future query
    // ailleurs qui re-introduit la fuite), on refuse explicitement de copier
    // des permissions stale dans useAdminStore. Sans ce check, un fetch lent
    // sur la nouvelle queryKey laisserait useAdminBootstrap copier les
    // permissions de l'user precedent sous le userId du nouvel user.
    if (isPlaceholderData) return;
    setPermissions(permissions, userId, userRole);
  }, [isAuthenticated, userId, userRole, permissions, isPlaceholderData, setPermissions]);

  useEffect(() => {
    if (isAuthenticated) return;
    // Logout : libérer le scope EN PREMIER, sinon le setState des resets ci-dessous
    // déclenche le persist middleware qui écrirait les valeurs par défaut dans la
    // clé `event-filters:<userId>` (et équivalents) — écrasant les données
    // mémorisées de l'user qui se déconnecte. Avec `currentUserId = null`, le
    // `userScopedStorage.setItem` devient no-op et le localStorage reste intact
    // → retrouvailles propres au prochain login du même user.
    clearUserScope();
    clear();
    resetEventFiltersStore();
    resetFormStore();
    resetFormEntriesStore();
  }, [isAuthenticated, clear]);
};
